import React, { useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  RowData,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { Flex } from "@radix-ui/themes";
import { ArrowDownIcon, ArrowUpIcon } from "@radix-ui/react-icons";
import * as ShadcnTable from "@/v2/shared/shadcn-ui/table";
import { actionsCell, defaultCellRenderer } from "./cellRenderers";
import { TextWithTooltip } from "@/components/shared/tooltip/TextWithTooltip";
import { LoadingCell } from "./LoadingCell";

type RowActionFn<RowDataType extends RowData> = (
  row: RowDataType
) => void | Promise<void>;

type RowAction<RowDataType extends RowData> = {
  label: string;
  icon: React.ReactNode;
  onClick: RowActionFn<RowDataType>;
};

export type RowActions<RowDataType extends RowData> = RowAction<RowDataType>[];

declare module "@tanstack/table-core" {
  interface TableMeta<TData extends RowData> {
    rowActions?: RowActions<TData>;
    getRowActions?: (row: TData) => RowActions<TData>;
  }
}

interface SimpleTableProps<RowDataType extends RowData> {
  data: RowDataType[];
  columns: ColumnDef<RowDataType>[];
  loading?: boolean;
  onRowClicked?: (row: RowDataType) => void;
  rowActions?: RowActions<RowDataType>;
  getRowActions?: (row: RowDataType) => RowActions<RowDataType>;
  defaultSorting?: SortingState;
  selectedRowId?: string | null;
  getRowId?: (row: RowDataType) => string;
}

export function SimpleTable<RowDataType extends RowData>({
  data,
  rowActions,
  getRowActions,
  loading,
  defaultSorting = [],
  selectedRowId,
  getRowId,
  ...props
}: SimpleTableProps<RowDataType>) {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);

  const columns = useMemo(() => {
    if (getRowActions == null && rowActions == null) {
      return props.columns;
    }

    return [
      ...props.columns,
      {
        id: "actions",
        header: "",
        cell: actionsCell<RowDataType>,
        meta: {
          className: "w-[40px]",
        },
      },
    ];
  }, [props.columns]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    defaultColumn: {
      cell: defaultCellRenderer<RowDataType>,
    },
    meta: {
      rowActions,
      getRowActions,
    },
  });

  return (
    <ShadcnTable.Table className="table-fixed border-collapse">
      <ShadcnTable.TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <ShadcnTable.TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const sortDirection = header.column.getIsSorted();
              const { className } = (header.column.columnDef.meta ?? {}) as {
                className?: string;
              };

              return (
                <ShadcnTable.TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  onClick={header.column.getToggleSortingHandler()}
                  className={className}
                >
                  {header.isPlaceholder ? null : (
                    <Flex gap="1" align="center">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}

                      {sortDirection === "asc" && <ArrowUpIcon />}
                      {sortDirection === "desc" && <ArrowDownIcon />}
                    </Flex>
                  )}
                </ShadcnTable.TableHead>
              );
            })}
          </ShadcnTable.TableRow>
        ))}
      </ShadcnTable.TableHeader>

      <ShadcnTable.TableBody>
        {loading ? (
          [...Array(10)].map((_, index) => (
            <ShadcnTable.TableRow key={index}>
              <ShadcnTable.TableCell>
                <LoadingCell index={index} />
              </ShadcnTable.TableCell>
            </ShadcnTable.TableRow>
          ))
        ) : (
          <>
            {table.getRowModel().rows.map((row) => {
              const rowId = getRowId
                ? getRowId(row.original)
                : (row.original as { id?: string }).id;
              const isSelected = selectedRowId && rowId === selectedRowId;

              return (
                <ShadcnTable.TableRow
                  key={row.id}
                  onClick={() => props.onRowClicked?.(row.original)}
                  style={
                    isSelected
                      ? {
                          backgroundColor: "var(--gray-3)",
                          borderRadius: "8px",
                        }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell, _, array) => {
                    const { cellTooltip = true, cellTooltipContent } = (cell.column.columnDef
                      .meta ?? {}) as {
                      cellTooltip?: boolean;
                      cellTooltipContent?: (row: RowDataType) => React.ReactNode;
                    };
                    const tooltipContent = cellTooltipContent?.(row.original);
                    const shouldShowTooltip =
                      cell.column.id !== "actions" && (cellTooltip || !!tooltipContent);

                    return (
                      <ShadcnTable.TableCell key={cell.id} className="truncate">
                        <TextWithTooltip
                          size="1"
                          color="gray"
                          highContrast
                          truncate
                          showTooltip={shouldShowTooltip}
                          tooltipContent={tooltipContent}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TextWithTooltip>
                      </ShadcnTable.TableCell>
                    );
                  })}
                </ShadcnTable.TableRow>
              );
            })}
          </>
        )}
      </ShadcnTable.TableBody>
    </ShadcnTable.Table>
  );
}
